import moment from 'moment';
import React, { useMemo } from 'react';
import TimelineChart, {
  DataColor,
  TimelineChartDataPoint,
} from '../../../../components/voxel/Charts/TimelineChart';
import { ForwardedChartProps } from '../ProgressDashboardSite/ProgressDashboardSite';
import { CompetitorData } from './models';
import Legend, { DataSeriesInfo } from '../Legend';

export interface ProgressDashboardCompetitorChartWeightedRankProps {
  competitorDataSeries: CompetitorData[];
  timelineChartProps: ForwardedChartProps;
  legend?: boolean;
}

export const ProgressDashboardCompetitorChartWeightedRank: React.FC<ProgressDashboardCompetitorChartWeightedRankProps> =
  ({ competitorDataSeries, timelineChartProps, legend }) => {
    const dataSeries: TimelineChartDataPoint[][] = useMemo(
      () =>
        competitorDataSeries.map((competitorDataPoints) =>
          competitorDataPoints.data.map(
            (dataPoint): TimelineChartDataPoint => ({
              date: moment(dataPoint.scheduledTime).toDate(),
              value: dataPoint.weightedAverageRank,
            })
          )
        ),
      [competitorDataSeries]
    );
    const dataColors: DataColor[] = useMemo(
      () =>
        competitorDataSeries.map((d) => ({
          fillClass: 'fill-none',
          strokeClass: d.color.darkStroke,
          tooltipInnerCircleClass: d.color.darkFill,
          tooltipOuterCircleClass: d.color.lightFill,
        })),
      [competitorDataSeries]
    );

    const dataSeriesInfo: DataSeriesInfo[] = useMemo(
      () =>
        competitorDataSeries.map(
          (d): DataSeriesInfo => ({
            name: d.domain.name,
            fillColorClass: d.color.darkFill,
            startValue: d.data[0]?.weightedAverageRank,
            latestValue: d.data[d.data.length - 1]?.weightedAverageRank,
          })
        ),
      [competitorDataSeries]
    );

    if (!competitorDataSeries?.length) return null;

    return (
      <div className="flex flex-row items-center w-full">
        <TimelineChart
          dataColors={dataColors}
          dataSeries={dataSeries}
          disableArea
          reverseY
          yLabelTooltip="Rank"
          yMinPlaceholder={1}
          yMaxPlaceholder={100}
          {...timelineChartProps}
        />

        {legend && (
          <div className="w-[280px] pl-6">
            <Legend
              loadingPlaceholder={timelineChartProps.loadingPlaceholder}
              dataSeriesInfo={dataSeriesInfo}
              widthName="w-[180px]"
              widthLatest="w-[50px]"
              widthChange="w-[50px]"
            />
          </div>
        )}
      </div>
    );
  };

export default ProgressDashboardCompetitorChartWeightedRank;
